import { theme } from "@tm/utility/global";
import React, { useCallback } from "react";
import { ChevronUpIcon } from "../../../../icons";
import { Card, CardPadding } from "../../Claims.style";
import { useClaims } from "../../ClaimsContext";
import { useClaimModal } from "../../modalManager";
import { ModalActionId } from "../../modalManager/types";
import type {
  ClaimCategory,
  ClaimContentType,
  ClaimInfoViewParams,
  ClaimReasonNode,
  RaiseNewClaimCardProps,
} from "../../types";
import {
  CardTitle,
  CategoryIcon,
  CategoryRow,
  CategoryTitle,
  ChevronWrapper,
  DisclaimerBox,
  PaxChevron,
  PaxTrigger,
  PaxWrapper,
  PolicyCardContainer,
  PolicyCardLeft,
  PolicyCardRight,
  PolicyCardRoute,
  PolicyCardTitle,
  PromptText,
} from "./RaiseNewClaimCard.style";

export const RaiseNewClaimCard: React.FC<RaiseNewClaimCardProps> = ({
  title,
  policyCard,
  claimPrompt,
  claimCategories = [],
  disclaimerBox,
}) => {
  const { navigateToInfo } = useClaims();
  const { openModal } = useClaimModal();

  const handleCategoryNavigation = useCallback(
    (item: ClaimReasonNode | ClaimCategory, parentCategoryTitle?: string) => {
      const { contentType, detailFetchParams } = item;
      if (!contentType || !detailFetchParams) return;

      if (contentType === "staticPage") {
        const params: ClaimInfoViewParams = {
          detailFetchParams,
          categoryTitle: item.title?.text?.value,
          categorySubtitle: parentCategoryTitle,
          categoryIcon:
            "icon" in item ? (item as ClaimCategory).icon : undefined,
          contentType: contentType as ClaimContentType,
        };
        navigateToInfo(params);
      }
    },
    [navigateToInfo]
  );

  const handleCategoryClick = useCallback(
    (cat: ClaimCategory) => {
      const hasReasonNodes =
        cat.reasonData?.nodes != null && cat.reasonData.nodes.length > 0;

      if (hasReasonNodes && cat.reasonData) {
        const catTitle = cat.title?.text?.value;
        openModal(ModalActionId.OPEN_CLAIM_REASON, {
          reasonData: cat.reasonData,
          onSelectNode: (node: ClaimReasonNode) =>
            handleCategoryNavigation(node, catTitle),
        });
      } else if (cat.contentType) {
        handleCategoryNavigation(cat);
      }
    },
    [openModal, handleCategoryNavigation]
  );

  const handlePaxClick = useCallback(() => {
    if (policyCard?.viewDetailData) {
      openModal(ModalActionId.VIEW_TRAVELLER_DETAIL, policyCard.viewDetailData);
    }
  }, [openModal, policyCard?.viewDetailData]);

  return (
    <Card>
      <CardPadding>
        <CardTitle>{title?.text?.value ?? "Raise New Claim"}</CardTitle>

        {policyCard && (
          <PolicyCardContainer
            $bgColor={policyCard.style?.backgroundColor}
            $borderRadius={policyCard.style?.cornerRadius}
          >
            <PolicyCardLeft>
              <PolicyCardTitle>
                {policyCard.provider?.text?.value}
              </PolicyCardTitle>
              <PolicyCardRoute>
                {policyCard.route?.text?.value}
                {policyCard.travelDates?.text?.value ? (
                  <>
                    {" | "}
                    {policyCard.travelDates?.text?.value}
                  </>
                ) : null}
              </PolicyCardRoute>
            </PolicyCardLeft>

            {policyCard.pax?.text?.value ? (
              <PolicyCardRight>
                <PaxTrigger
                  onClick={handlePaxClick}
                  type="button"
                  aria-label="View traveller details"
                  disabled={!policyCard.viewDetailData}
                >
                  <PaxWrapper>
                    {policyCard.pax?.text?.value}
                    {policyCard.viewDetailData && (
                      <PaxChevron aria-hidden="true">
                        <ChevronUpIcon
                          color={theme.blueText}
                          cursor="pointer"
                        />
                      </PaxChevron>
                    )}
                  </PaxWrapper>
                </PaxTrigger>
              </PolicyCardRight>
            ) : null}
          </PolicyCardContainer>
        )}

        {claimPrompt?.text?.value ? (
          <PromptText>{claimPrompt?.text?.value}</PromptText>
        ) : null}

        {claimCategories.map((cat: ClaimCategory, index: number) => (
          <CategoryRow
            key={cat.id ?? cat.title?.text?.value ?? index}
            role="button"
            tabIndex={0}
            onClick={() => handleCategoryClick(cat)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCategoryClick(cat);
              }
            }}
          >
            {cat.icon && <CategoryIcon src={cat.icon} alt="" />}
            <CategoryTitle>{cat.title?.text?.value}</CategoryTitle>
            {cat.showChevron !== false && (
              <ChevronWrapper aria-hidden="true">
                <ChevronUpIcon color={theme.blueText} cursor="default" />
              </ChevronWrapper>
            )}
          </CategoryRow>
        ))}

        {disclaimerBox?.message?.text?.value ? (
          <DisclaimerBox $bgColor={disclaimerBox?.style?.backgroundColor}>
            {disclaimerBox?.message?.text?.value}
          </DisclaimerBox>
        ) : null}
      </CardPadding>
    </Card>
  );
};
